/**
 * End Administration Modal
 * 
 * Modal for completing the second step of two-step medication administration.
 * Includes dose confirmation, vital signs collection, and patient response recording.
 */

import React, { useState, useEffect } from 'react';
import {
    Modal,
    Form,
    Input,
    Alert,
    Space,
    Button,
    Divider,
    Row,
    Col,
    Card,
    Select,
    Tag,
    DatePicker,
    message
} from 'antd';
import {
    UserOutlined,
    CheckCircleOutlined,
    MedicineBoxOutlined,
    ExclamationCircleOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';

import { validateAdministrationWindow } from '../../../utils/administrationValidation';
import { getCurrentUserId, getCurrentUserName } from '../../../utils/authUtils';

dayjs.extend(duration);

const { TextArea } = Input;
const { Option } = Select;

const EndAdministrationModal = ({
    open,
    session,
    patient,
    medication,
    onEnd,
    onCancel,
    zIndex = 15000 // Very high z-index to ensure modal appears above everything
}) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [vitals, setVitals] = useState([]);
    const [elapsedTime, setElapsedTime] = useState(null);
    const [isHandoff, setIsHandoff] = useState(false);
    const [administrationTime, setAdministrationTime] = useState(null);
    const [administrationStatus, setAdministrationStatus] = useState(null);
    const [requiresJustification, setRequiresJustification] = useState(false);
    const [justificationReason, setJustificationReason] = useState('');

    // Real-time status calculation for industry-standard categories
    const calculateAdministrationStatus = (administeredTime, scheduledTime) => {
        if (!administeredTime || !scheduledTime) return null;

        const timeDiff = dayjs(administeredTime).diff(dayjs(scheduledTime), 'minutes');

        if (timeDiff < -30) {
            return 'administered_early';
        } else if (timeDiff >= -30 && timeDiff <= 30) {
            return 'administered_on_time';
        } else {
            return 'administered_late';
        }
    };

    // Status display configuration
    const getStatusConfig = (status) => {
        const configs = {
            'administered_early': {
                text: 'Administered Early',
                color: 'warning',
                bgColor: '#fff7e6',
                description: 'More than 30 minutes before scheduled time'
            },
            'administered_on_time': {
                text: 'Administered On Time',
                color: 'success',
                bgColor: '#f6ffed',
                description: 'Within 30 minutes of scheduled time'
            },
            'administered_late': {
                text: 'Administered Late',
                color: 'error',
                bgColor: '#fff2f0',
                description: 'More than 30 minutes after scheduled time'
            }
        };
        return configs[status] || null;
    };

    // Justification reason options
    const justificationReasons = [
        { value: 'early_clinical_need', label: 'Clinical necessity - patient condition required early administration' },
        { value: 'early_schedule_conflict', label: 'Schedule conflict - avoiding missed dose due to procedures' },
        { value: 'early_patient_request', label: 'Patient request due to pain/discomfort' },
        { value: 'late_patient_unavailable', label: 'Patient unavailable at scheduled time' },
        { value: 'late_emergency_situation', label: 'Emergency situation delayed administration' },
        { value: 'late_medication_preparation', label: 'Medication preparation took longer than expected' },
        { value: 'late_staff_handoff', label: 'Delay due to staff handoff or shift change' },
        { value: 'timing_correction', label: 'Correcting previous timing error' },
        { value: 'other', label: 'Other (specify in notes)' }
    ];

    // Vital signs options
    const vitalTypes = [
        { value: 'blood_pressure', label: 'Blood Pressure', unit: 'mmHg' },
        { value: 'heart_rate', label: 'Heart Rate', unit: 'bpm' },
        { value: 'respiratory_rate', label: 'Respiratory Rate', unit: '/min' },
        { value: 'temperature', label: 'Temperature', unit: '°F' },
        { value: 'oxygen_saturation', label: 'Oxygen Saturation', unit: '%' },
        { value: 'blood_glucose', label: 'Blood Glucose', unit: 'mg/dL' },
        { value: 'pain_scale', label: 'Pain Scale', unit: '0-10' }
    ];

    // Patient response options
    const responseOptions = [
        'Patient tolerated well',
        'No adverse effects observed',
        'Mild nausea reported',
        'Pain relief noted',
        'Patient requested water',
        'Patient fell asleep',
        'Other (see notes)'
    ];

    useEffect(() => {
        if (open && session) {
            const currentUserId = getCurrentUserId();
            const currentUserName = getCurrentUserName();
            const isHandoffSession = session.start_nurse_id !== currentUserId;

            setIsHandoff(isHandoffSession);

            // Initialize form with current date and time
            const defaultTime = dayjs();
            setAdministrationTime(defaultTime);
            form.setFieldsValue({
                nurse_id: currentUserId,
                nurse_name: currentUserName,
                dose_administered: medication?.dose_amount || '1 tablet',
                administration_time: defaultTime
            });

            // Calculate elapsed time
            if (session.start_time) {
                const startTime = dayjs(session.start_time);
                const now = dayjs();
                const elapsed = dayjs.duration(now.diff(startTime));
                setElapsedTime(elapsed);
            }

            // Preload required post-administration vitals from workflow/medication
            const requiredPostVitals = Array.isArray(medication?.post_administration_vitals)
                ? medication.post_administration_vitals
                : [];

            if (requiredPostVitals.length > 0) {
                setVitals(requiredPostVitals.map((vt) => {
                    const unit = vitalTypes.find(v => v.value === vt)?.unit || '';
                    return {
                        id: Date.now() + Math.random(),
                        type: vt,
                        value: '',
                        unit,
                        notes: ''
                    };
                }));
            } else {
                // Clear vitals if none required
                setVitals([]);
            }
        }
    }, [open, session, form, medication]);

    // Real-time status calculation when administration time changes
    useEffect(() => {
        if (administrationTime && session?.scheduled_time) {
            const status = calculateAdministrationStatus(administrationTime, session.scheduled_time);
            setAdministrationStatus(status);

            // Check if justification is required (early or late administration)
            const needsJustification = status === 'administered_early' || status === 'administered_late';
            setRequiresJustification(needsJustification);

            if (!needsJustification) {
                setJustificationReason('');
                form.setFieldsValue({
                    justification_reason: undefined,
                    justification_notes: ''
                });
            }
        } else {
            setAdministrationStatus(null);
            setRequiresJustification(false);
        }
    }, [administrationTime, session?.scheduled_time, form]);

    const addVital = () => {
        setVitals([...vitals, {
            id: Date.now(),
            type: '',
            value: '',
            unit: '',
            notes: ''
        }]);
    };

    const removeVital = (id) => {
        setVitals(vitals.filter(v => v.id !== id));
    };

    const updateVital = (id, field, value) => {
        setVitals(vitals.map(v =>
            v.id === id
                ? {
                    ...v,
                    [field]: value,
                    // Auto-update unit when type changes
                    ...(field === 'type' ? {
                        unit: vitalTypes.find(vt => vt.value === value)?.unit || ''
                    } : {})
                }
                : v
        ));
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            // Prepare vitals data
            const validVitals = vitals
                .filter(v => v.type && v.value)
                .map(v => ({
                    type: v.type,
                    value: v.value,
                    unit: v.unit || vitalTypes.find(vt => vt.value === v.type)?.unit || '',
                    measured_at: dayjs().toISOString(),
                    measured_by: values.nurse_id,
                    notes: v.notes || null
                }));

            const endData = {
                nurse_id: values.nurse_id,
                nurse_name: values.nurse_name,
                notes: values.notes || null,
                vitals: validVitals,
                dose_administered: values.dose_administered,
                patient_response: values.patient_response || null,
                administration_time: values.administration_time ? dayjs(values.administration_time).toISOString() : dayjs().toISOString(),
                // Include calculated administration status
                administration_status: administrationStatus,
                // Include justification if required
                ...(requiresJustification && {
                    justification: {
                        reason: values.justification_reason,
                        notes: values.justification_notes || null,
                        justified_by: values.nurse_id,
                        justified_at: dayjs().toISOString()
                    }
                })
            };

            console.log('End Administration - Form values:', values);
            console.log('End Administration - Selected time:', values.administration_time?.format('YYYY-MM-DD HH:mm:ss'));
            console.log('End Administration - ISO time being sent:', endData.administration_time);
            console.log('End Administration - Full payload:', endData);

            console.log('End Administration - About to call onEnd with:', endData);
            await onEnd(endData);
            console.log('End Administration - onEnd completed successfully');
            form.resetFields();
            setVitals([]);
            message.success('Administration completed successfully');
        } catch (error) {
            console.error('End administration error:', error);
            const status = error?.response?.status;
            if (status === 403) {
                message.error(error?.response?.data?.detail || 'You do not have permission to complete administration.');
            } else {
                message.error(error.message || 'Failed to complete administration');
            }
        } finally {
            setLoading(false);
        }
    };

    const renderSessionInfo = () => (
        <Card size="small" title="Administration Session Info" style={{ marginBottom: 16 }}>
            <Row gutter={[16, 8]}>
                <Col xs={24} sm={12}>
                    <div><strong>Started by:</strong> {session?.start_nurse_name || 'Unknown'}</div>
                    <div><strong>Started at:</strong> {session?.start_time ? dayjs(session.start_time).format('hh:mm A') : 'Not started'}</div>
                </Col>
                <Col xs={24} sm={12}>
                    {elapsedTime && (
                        <div><strong>Elapsed time:</strong> {elapsedTime.format('H[h] m[m]')}</div>
                    )}
                    {isHandoff && (
                        <div>
                            <Tag color="orange" icon={<ExclamationCircleOutlined />}>
                                Nurse Handoff
                            </Tag>
                        </div>
                    )}
                </Col>
            </Row>
        </Card>
    );

    // Real-time status display
    const renderStatusSection = () => {
        if (!administrationStatus || !session?.scheduled_time) return null;

        const statusConfig = getStatusConfig(administrationStatus);
        if (!statusConfig) return null;

        const timeDiff = dayjs(administrationTime).diff(dayjs(session.scheduled_time), 'minutes');
        const diffText = timeDiff === 0 ? 'exactly on time' :
                        timeDiff > 0 ? `${timeDiff} minutes late` :
                        `${Math.abs(timeDiff)} minutes early`;

        return (
            <Card
                size="small"
                title="Administration Status"
                style={{
                    marginBottom: 16,
                    backgroundColor: statusConfig.bgColor,
                    border: `2px solid ${statusConfig.color === 'success' ? '#52c41a' : statusConfig.color === 'warning' ? '#fa8c16' : '#ff4d4f'}`
                }}
            >
                <Row gutter={[16, 8]}>
                    <Col xs={24} sm={12}>
                        <div>
                            <Tag color={statusConfig.color} style={{ fontSize: '14px', padding: '4px 12px' }}>
                                {statusConfig.text}
                            </Tag>
                        </div>
                        <div style={{ marginTop: 8, color: '#666' }}>
                            {statusConfig.description}
                        </div>
                    </Col>
                    <Col xs={24} sm={12}>
                        <div><strong>Scheduled time:</strong> {dayjs(session.scheduled_time).format('hh:mm A')}</div>
                        <div><strong>Administration time:</strong> {dayjs(administrationTime).format('hh:mm A')}</div>
                        <div><strong>Time difference:</strong> {diffText}</div>
                    </Col>
                </Row>

                {requiresJustification && (
                    <Alert
                        message="Justification Required"
                        description="Administration is outside standard timing window. Please provide justification below."
                        type="warning"
                        showIcon
                        style={{ marginTop: 12 }}
                    />
                )}
            </Card>
        );
    };

    const renderVitalsSection = () => (
        <Card size="small" title="Post-Administration Vitals" style={{ marginBottom: 16 }}>
            {/* <div style={{ marginBottom: 12 }}>
                <Button onClick={addVital} size="small">
                    Add Vital Sign
                </Button>
            </div> */}

            {vitals.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#999', padding: '20px 0' }}>
                    No vital signs added. Click "Add Vital Sign" to record post-administration vitals.
                </div>
            ) : (
                vitals.map((vital) => (
                    <Card key={vital.id} size="small" style={{ marginBottom: 8 }}>
                        <Row gutter={[8, 8]} align="middle">
                            <Col xs={24} sm={6}>
                                <Select
                                    placeholder="Select vital"
                                    getPopupContainer={(trigger) => trigger?.closest('.ant-modal') || document.body}
                                    dropdownMatchSelectWidth={false}
                                    dropdownStyle={{ zIndex: 16000, pointerEvents: 'auto' }}
                                    value={vital.type}
                                    onChange={(value) => updateVital(vital.id, 'type', value)}
                                    size="small"
                                    style={{ width: '100%' }}
                                >
                                    {vitalTypes.map(vt => (
                                        <Option key={vt.value} value={vt.value}>
                                            {vt.label}
                                        </Option>
                                    ))}
                                </Select>
                            </Col>
                            <Col xs={12} sm={4}>
                                <Input
                                    placeholder="Value"
                                    value={vital.value}
                                    onChange={(e) => updateVital(vital.id, 'value', e.target.value)}
                                    size="small"
                                />
                            </Col>
                            <Col xs={12} sm={3}>
                                <Input
                                    placeholder="Unit"
                                    value={vital.unit}
                                    onChange={(e) => updateVital(vital.id, 'unit', e.target.value)}
                                    size="small"
                                />
                            </Col>
                            <Col xs={20} sm={8}>
                                <Input
                                    placeholder="Notes (optional)"
                                    value={vital.notes}
                                    onChange={(e) => updateVital(vital.id, 'notes', e.target.value)}
                                    size="small"
                                />
                            </Col>
                            <Col xs={4} sm={3}>
                                <Button
                                    danger
                                    size="small"
                                    onClick={() => removeVital(vital.id)}
                                >
                                    Remove
                                </Button>
                            </Col>
                        </Row>
                    </Card>
                ))
            )}
        </Card>
    );

    if (!session) return null;

    return (
        <>
            {/* Force high z-index for administration modals */}
            <style>
                {`
                .administration-modal-wrapper .ant-modal-wrap {
                    z-index: 15000 !important;
                    position: fixed !important;
                    display: flex !important;
                    align-items: center !important;
                    justify-content: center !important;
                }
                .administration-modal-wrapper .ant-modal-mask {
                    z-index: 14999 !important;
                    position: fixed !important;
                    background: rgba(0, 0, 0, 0.7) !important;
                }
                .administration-modal-wrapper .ant-modal {
                    z-index: 15000 !important;
                    position: relative !important;
                    margin: 0 auto !important;
                    top: auto !important;
                    left: auto !important;
                    transform: none !important;
                }
                .administration-modal-wrapper {
                    z-index: 15000 !important;
                    position: fixed !important;
                }
                .administration-modal-wrapper .ant-modal-content {
                    pointer-events: auto !important;
                }
                .administration-modal-wrapper .ant-input,
                .administration-modal-wrapper .ant-select,
                .administration-modal-wrapper .ant-form-item {
                    pointer-events: auto !important;
                }
                `}
            </style>
            <Modal
            title={
                <Space>
                    <CheckCircleOutlined />
                    Complete Administration - Step 2 of 2
                </Space>
            }
            open={open}
            onCancel={onCancel}
            width={800}
            zIndex={zIndex}
            mask={true}
            maskClosable={false}
            getContainer={() => document.body}
            style={{ zIndex: zIndex }}
            styles={{ mask: { zIndex: 14999 } }}
            wrapClassName="administration-modal-wrapper"
            destroyOnHidden={false}
            forceRender={true}
            focusTriggerAfterClose={false}
            footer={[
                <Button key="cancel" onClick={onCancel}>
                    Cancel
                </Button>,
                <Button
                    key="complete"
                    type="primary"
                    loading={loading}
                    onClick={handleSubmit}
                >
                    Complete Administration
                </Button>
            ]}
        >
            <Alert
                message="Completing Two-Step Administration"
                description={`Completing administration for ${medication?.medication_name || medication?.brand_name}. This will finalize the administration process and record the completion.`}
                type="success"
                showIcon
                style={{ marginBottom: 16 }}
            />

            {isHandoff && (
                <Alert
                    message="Nurse Handoff"
                    description="You are completing an administration started by another nurse. Please verify all details before proceeding."
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                />
            )}

            {renderSessionInfo()}

            {renderStatusSection()}

            <Row gutter={[16, 16]}>
                <Col xs={24} md={12}>
                    <Card size="small" title="Patient Information">
                        <div><strong>Name:</strong> {patient?.full_name}</div>
                        <div><strong>MRN:</strong> {patient?.mrn}</div>
                        <div><strong>Age:</strong> {patient?.age} years</div>
                    </Card>
                </Col>

                <Col xs={24} md={12}>
                    <Card size="small" title="Medication Information">
                        <div><strong>Medication:</strong> {medication?.medication_name || medication?.brand_name}</div>
                        <div><strong>Route:</strong> {medication?.route || medication?.route_of_administration}</div>
                        <div><strong>Prescribed dose:</strong> {medication?.dose_amount}</div>
                    </Card>
                </Col>
            </Row>

            <Divider />

            <Form form={form} layout="vertical">
                <Row gutter={[16, 0]}>
                    <Col xs={24} sm={12}>
                        <Form.Item
                            label="Completing Nurse ID"
                            name="nurse_id"
                            rules={[{ required: true, message: 'Nurse ID is required' }]}
                        >
                            <Input
                                prefix={<UserOutlined />}
                                placeholder="Enter your nurse ID"
                                readOnly
                                style={{ backgroundColor: '#f5f5f5' }}
                            />
                        </Form.Item>
                    </Col>

                    <Col xs={24} sm={12}>
                        <Form.Item
                            label="Completing Nurse Name"
                            name="nurse_name"
                            rules={[{ required: true, message: 'Nurse name is required' }]}
                        >
                            <Input
                                placeholder="Enter your full name"
                                readOnly
                                style={{ backgroundColor: '#f5f5f5' }}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={[16, 0]}>
                    <Col xs={24} sm={8}>
                        <Form.Item
                            label="Actual Dose Administered"
                            name="dose_administered"
                            rules={[{ required: true, message: 'Administered dose is required' }]}
                        >
                            <Input
                                prefix={<MedicineBoxOutlined />}
                                placeholder="e.g., 1 tablet, 5ml, etc."
                            />
                        </Form.Item>
                    </Col>

                    <Col xs={24} sm={8}>
                        <Form.Item
                            label="Patient Response"
                            name="patient_response"
                        >
                            <Select
                                placeholder="Select patient response"
                                allowClear
                                getPopupContainer={(trigger) => trigger?.closest('.ant-modal') || document.body}
                                dropdownMatchSelectWidth={false}
                                dropdownStyle={{ zIndex: 16000, pointerEvents: 'auto' }}
                            >
                                {responseOptions.map(option => (
                                    <Option key={option} value={option}>
                                        {option}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>

                    <Col xs={24} sm={8}>
                        <Form.Item
                            label="Administration Date & Time"
                            name="administration_time"
                            rules={[
                                { required: true, message: 'Administration time is required' },
                                {
                                    validator: (_, value) => {
                                        if (!value || !session?.start_time) {
                                            return Promise.resolve();
                                        }
                                        const endTime = dayjs(value);
                                        const startTime = dayjs(session.start_time);
                                        if (endTime.isBefore(startTime)) {
                                            return Promise.reject(new Error('End time must be after start time'));
                                        }
                                        return Promise.resolve();
                                    }
                                }
                            ]}
                        >
                            <div style={{ width: '100%' }}>
                                <DatePicker
                                    showTime
                                    format="YYYY-MM-DD HH:mm:ss"
                                    placeholder="Select administration time"
                                    style={{ width: '100%', marginBottom: '8px' }}
                                    getPopupContainer={(trigger) => trigger?.closest('.ant-modal') || document.body}
                                    popupStyle={{ zIndex: 16000, pointerEvents: 'auto' }}
                                    value={administrationTime}
                                    onChange={(value) => {
                                        setAdministrationTime(value);
                                        form.setFieldsValue({ administration_time: value });
                                    }}
                                    disabledDate={(current) => {
                                        // Disable dates that are more than 24 hours old OR future dates
                                        const now = dayjs();
                                        const twentyFourHoursAgo = now.subtract(24, 'hours');
                                        const startTime = session?.start_time ? dayjs(session.start_time) : null;

                                        // Basic date constraints
                                        const basicConstraints = current && (current.isAfter(now, 'day') || current.isBefore(twentyFourHoursAgo, 'day'));

                                        // Start time constraint - disable dates before start date
                                        const startConstraint = startTime && current && current.isBefore(startTime, 'day');

                                        return basicConstraints || startConstraint;
                                    }}
                                    disabledTime={(current) => {
                                        if (!current) return {};

                                        const now = dayjs();
                                        const twentyFourHoursAgo = now.subtract(24, 'hours');
                                        const currentDate = dayjs(current);
                                        const startTime = session?.start_time ? dayjs(session.start_time) : null;

                                        console.log('DisabledTime - Current date:', currentDate.format('YYYY-MM-DD HH:mm:ss'));
                                        console.log('DisabledTime - Start time:', startTime?.format('YYYY-MM-DD HH:mm:ss'));
                                        console.log('DisabledTime - Now:', now.format('YYYY-MM-DD HH:mm:ss'));

                                        // CONSOLIDATED TIME VALIDATION - Apply all constraints together
                                        return {
                                            disabledHours: () => {
                                                const hours = [];

                                                // 1. START TIME CONSTRAINT: Disable hours before start time
                                                if (startTime) {
                                                    if (currentDate.isSame(startTime, 'day')) {
                                                        // Same day as start - disable all hours before start hour
                                                        for (let i = 0; i < startTime.hour(); i++) {
                                                            hours.push(i);
                                                        }
                                                        console.log('DisabledTime - Hours disabled due to start time (same day):',
                                                            Array.from({ length: startTime.hour() }, (_, i) => i));
                                                    } else if (currentDate.isBefore(startTime, 'day')) {
                                                        // Before start date - disable all hours
                                                        for (let i = 0; i <= 23; i++) {
                                                            hours.push(i);
                                                        }
                                                        console.log('DisabledTime - All hours disabled (before start date)');
                                                    }
                                                }

                                                // 2. FUTURE TIME CONSTRAINT: If it's today, disable future hours
                                                if (currentDate.isSame(now, 'day')) {
                                                    for (let i = now.hour() + 1; i <= 23; i++) {
                                                        if (!hours.includes(i)) {
                                                            hours.push(i);
                                                        }
                                                    }
                                                    console.log('DisabledTime - Future hours disabled (today):',
                                                        Array.from({ length: 23 - now.hour() }, (_, i) => now.hour() + 1 + i));
                                                }

                                                // 3. 24-HOUR WINDOW: If it's 24 hours ago date, disable early hours
                                                if (currentDate.isSame(twentyFourHoursAgo, 'day')) {
                                                    for (let i = 0; i < twentyFourHoursAgo.hour(); i++) {
                                                        if (!hours.includes(i)) {
                                                            hours.push(i);
                                                        }
                                                    }
                                                    console.log('DisabledTime - Early hours disabled (24h ago):',
                                                        Array.from({ length: twentyFourHoursAgo.hour() }, (_, i) => i));
                                                }

                                                console.log('DisabledTime - FINAL disabled hours:', hours.sort((a, b) => a - b));
                                                return hours;
                                            },

                                            disabledMinutes: (selectedHour) => {
                                                const minutes = [];
                                                console.log(`DisabledTime - Checking minutes for hour: ${selectedHour}`);

                                                // 1. START TIME CONSTRAINT
                                                if (startTime && currentDate.isSame(startTime, 'day') && selectedHour === startTime.hour()) {
                                                    for (let i = 0; i < startTime.minute(); i++) {
                                                        minutes.push(i);
                                                    }
                                                    console.log(`DisabledTime - Minutes disabled due to start time (${startTime.minute()}):`,
                                                        Array.from({ length: startTime.minute() }, (_, i) => i));
                                                }

                                                // 2. FUTURE TIME CONSTRAINT
                                                if (currentDate.isSame(now, 'day') && selectedHour === now.hour()) {
                                                    for (let i = now.minute() + 1; i <= 59; i++) {
                                                        if (!minutes.includes(i)) {
                                                            minutes.push(i);
                                                        }
                                                    }
                                                    console.log(`DisabledTime - Future minutes disabled (>${now.minute()}):`,
                                                        Array.from({ length: 59 - now.minute() }, (_, i) => now.minute() + 1 + i));
                                                }

                                                // 3. 24-HOUR WINDOW
                                                if (currentDate.isSame(twentyFourHoursAgo, 'day') && selectedHour === twentyFourHoursAgo.hour()) {
                                                    for (let i = 0; i < twentyFourHoursAgo.minute(); i++) {
                                                        if (!minutes.includes(i)) {
                                                            minutes.push(i);
                                                        }
                                                    }
                                                    console.log(`DisabledTime - Early minutes disabled (<${twentyFourHoursAgo.minute()}):`,
                                                        Array.from({ length: twentyFourHoursAgo.minute() }, (_, i) => i));
                                                }

                                                console.log(`DisabledTime - FINAL disabled minutes for hour ${selectedHour}:`, minutes.sort((a, b) => a - b));
                                                return minutes;
                                            },

                                            disabledSeconds: (selectedHour, selectedMinute) => {
                                                const seconds = [];
                                                console.log(`DisabledTime - Checking seconds for ${selectedHour}:${selectedMinute}`);

                                                // 1. START TIME CONSTRAINT: Disable seconds <= start time (not just <)
                                                if (startTime && currentDate.isSame(startTime, 'day') &&
                                                    selectedHour === startTime.hour() && selectedMinute === startTime.minute()) {
                                                    for (let i = 0; i <= startTime.second(); i++) {
                                                        seconds.push(i);
                                                    }
                                                    console.log(`DisabledTime - Seconds disabled due to start time (≤${startTime.second()}):`,
                                                        Array.from({ length: startTime.second() + 1 }, (_, i) => i));
                                                }

                                                // 2. FUTURE TIME CONSTRAINT
                                                if (currentDate.isSame(now, 'day') &&
                                                    selectedHour === now.hour() && selectedMinute === now.minute()) {
                                                    for (let i = now.second() + 1; i <= 59; i++) {
                                                        if (!seconds.includes(i)) {
                                                            seconds.push(i);
                                                        }
                                                    }
                                                    console.log(`DisabledTime - Future seconds disabled (>${now.second()}):`,
                                                        Array.from({ length: 59 - now.second() }, (_, i) => now.second() + 1 + i));
                                                }

                                                // 3. 24-HOUR WINDOW
                                                if (currentDate.isSame(twentyFourHoursAgo, 'day') &&
                                                    selectedHour === twentyFourHoursAgo.hour() && selectedMinute === twentyFourHoursAgo.minute()) {
                                                    for (let i = 0; i < twentyFourHoursAgo.second(); i++) {
                                                        if (!seconds.includes(i)) {
                                                            seconds.push(i);
                                                        }
                                                    }
                                                    console.log(`DisabledTime - Early seconds disabled (<${twentyFourHoursAgo.second()}):`,
                                                        Array.from({ length: twentyFourHoursAgo.second() }, (_, i) => i));
                                                }

                                                console.log(`DisabledTime - FINAL disabled seconds for ${selectedHour}:${selectedMinute}:`,
                                                    seconds.sort((a, b) => a - b));
                                                return seconds;
                                            }
                                        };
                                    }}
                                    showNow={false}
                                />
                                <Button
                                    size="small"
                                    type="link"
                                    onClick={() => {
                                        const now = dayjs();
                                        setAdministrationTime(now);
                                        form.setFieldsValue({
                                            administration_time: now
                                        });
                                        form.validateFields(['administration_time']);
                                    }}
                                    style={{ marginTop: '4px', padding: '0', height: 'auto' }}
                                >
                                    Set to Now
                                </Button>
                            </div>
                        </Form.Item>
                    </Col>
                </Row>

                {/* Justification Section - Shows when administration timing requires justification */}
                {requiresJustification && (
                    <>
                        <Divider style={{ margin: '16px 0' }}>Justification Required</Divider>

                        <Row gutter={[16, 0]}>
                            <Col xs={24} sm={12}>
                                <Form.Item
                                    label="Justification Reason"
                                    name="justification_reason"
                                    rules={[{ required: requiresJustification, message: 'Justification reason is required' }]}
                                >
                                    <Select
                                        placeholder="Select justification reason"
                                        getPopupContainer={(trigger) => trigger?.closest('.ant-modal') || document.body}
                                        dropdownMatchSelectWidth={false}
                                        dropdownStyle={{ zIndex: 16000, pointerEvents: 'auto' }}
                                        value={justificationReason}
                                        onChange={setJustificationReason}
                                    >
                                        {justificationReasons.map(reason => (
                                            <Option key={reason.value} value={reason.value}>
                                                {reason.label}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>

                            <Col xs={24} sm={12}>
                                <Form.Item
                                    label="Justification Notes"
                                    name="justification_notes"
                                    rules={[
                                        {
                                            required: justificationReason === 'other',
                                            message: 'Please specify the reason when selecting "Other"'
                                        }
                                    ]}
                                >
                                    <TextArea
                                        rows={3}
                                        placeholder="Additional details for justification (required if 'Other' selected)"
                                        maxLength={300}
                                        showCount
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                    </>
                )}

                <Form.Item
                    label="Completion Notes"
                    name="notes"
                >
                    <TextArea
                        rows={3}
                        placeholder="Enter any notes about completing this administration (optional)"
                        maxLength={500}
                        showCount
                    />
                </Form.Item>
            </Form>

            {renderVitalsSection()}
        </Modal>
        </>
    );
};

export default EndAdministrationModal;